import { Moon, Sun } from "lucide-react";

import { cn } from "@/lib/utils";
import { ColorField } from "@/components/color-field";
import { Label } from "@/components/ui/label";
import { SWATCHES } from "@/data/config";
import { usePortal } from "@/features/portal/portal-context";
import { useThemeStudio } from "@/features/theming/theme-studio-context";
import { PinVerdictNotice } from "@/features/theming/pin-verdict-notice";
import { FONT_CHOICES, TREATMENT_LIST, type Polarity } from "@/data/theme-engine";

function KnobRow({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <div>
        <Label className="text-[13px] font-semibold">{label}</Label>
        {hint && <p className="mt-0.5 text-[12px] text-text-3">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

function Segmented<T extends string>({ options, value, onChange }: {
  options: { id: T; label: string; sample?: React.ReactNode }[];
  value: T;
  onChange: (id: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          aria-pressed={value === o.id}
          onClick={() => onChange(o.id)}
          className={cn(
            "flex items-center gap-1.5 rounded-[10px] border px-3 py-1.5 text-[12.5px] font-semibold outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring",
            value === o.id
              ? "border-brand bg-brand/10 text-foreground"
              : "border-border bg-panel-2 text-muted-foreground hover:text-foreground",
          )}
        >
          {o.sample}
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * The 4 knobs — seed · light/dark · treatment · font. Each change re-runs the
 * real engine (via the applyDraftTreatment command) and updates the live app.
 *
 * NOTE (flagged for AJ): light/dark is coupled to treatment in today's registry
 * (every reflective treatment is light; only Night is dark), so toggling polarity
 * swaps to that polarity's default treatment. When an emissive-light / reflective-
 * dark treatment lands upstream the knob becomes fully independent for free.
 */
export function ThemeKnobs() {
  const { recipe, setSeed, setPolarity, setTreatment, setFont } = useThemeStudio();
  const { pinNotice } = usePortal();

  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
      <KnobRow label="Seed color" hint="Your brand colour — everything generates from it.">
        <ColorField value={recipe.seed} onChange={setSeed} swatches={SWATCHES} />
        <PinVerdictNotice verdicts={pinNotice} seed={recipe.seed} />
      </KnobRow>

      <KnobRow label="Light or dark">
        <Segmented<Polarity>
          value={recipe.polarity}
          onChange={setPolarity}
          options={[
            { id: "dark", label: "Dark", sample: <Moon className="size-3.5" /> },
            { id: "light", label: "Light", sample: <Sun className="size-3.5" /> },
          ]}
        />
      </KnobRow>

      <KnobRow label="Treatment" hint="The display material — how the app is lit.">
        <Segmented
          value={recipe.treatmentId}
          onChange={setTreatment}
          options={TREATMENT_LIST.map((t) => ({ id: t.id, label: t.name }))}
        />
      </KnobRow>

      <KnobRow label="Font">
        <Segmented
          value={recipe.fontId}
          onChange={setFont}
          options={FONT_CHOICES.map((f) => ({
            id: f.id,
            label: f.label,
            sample: <span style={{ fontFamily: f.previewFamily }} className="text-[13px]">Aa</span>,
          }))}
        />
      </KnobRow>
    </div>
  );
}
